import { CommonModule } from "@angular/common";
import { Component, inject, Input, OnInit } from "@angular/core";
import { FormGroup, ReactiveFormsModule } from "@angular/forms";
import { MatIconModule } from "@angular/material/icon";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { FormlyBootstrapModule } from "@ngx-formly/bootstrap";
import { FormlyFieldConfig, FormlyFormOptions, FormlyModule } from "@ngx-formly/core";
import { MessageService } from "primeng/api";
import { Observable } from "rxjs";
import { ConnectionCardComponent } from "../../../components/connection-card/connection-card.component";
import { LoadingComponent } from "../../../components/common/loading/loading.component";
import { NoDataComponent } from "../../../components/common/no-data/no-data.component";
import { AgentCreateModalComponent } from "../../../components/modal-templates/agent-create-modal/agent-create-modal.component";
import { ConfigureAssetLlmModalComponent } from "../../../components/modal-templates/configure-asset-llm-modal/configure-asset-llm-modal.component";
import { ConnectionModalComponent } from "../../../components/modal-templates/connection-modal/connection-modal.component";
import { DataDisplayModalComponent } from "../../../components/modal-templates/data-display-modal/data-display-modal.component";
import { DeleteConfirmationComponent } from "../../../components/modal-templates/delete-confirmation/delete-confirmation.component";
import { FormlyModalComponent } from "../../../components/modal-templates/formly-modal/formly-modal.component";
import { SetAppContextModalComponent } from "../../../components/modal-templates/set-app-context-modal/set-app-context-modal.component";
import {
  llamaConnFields,
  llamaConnFieldsEdit
} from "../../../core/dummy/dummy-data";
import {
  connectionFileTemplate,
  connectionJiraTemplate,
  connectionLLMTemplate,
} from "../../../core/generic-templates/formly-form-templates";
import { ApplicationDetailsActions } from "../../../core/ngrx/actions/application-details.actions";
import { ConnectionActions } from "../../../core/ngrx/actions/connection.actions";
import { selectApplicationDetails } from "../../../core/ngrx/selectors/application-details.selectors";
import {
  selectConnectionError,
  selectConnectionLoading,
  selectConnections,
} from "../../../core/ngrx/selectors/connection.selectors";
import { MaterialModule } from "../../../core/shared/material.module";
import { MessageEnum } from "../../../core/utils/message.enum";
import { DataStore } from "../../../core/models/data-store";
import { BaseLayoutComponent } from "../../base-layout.component";
/**
 * ConnectionComponent is responsible for managing and displaying
 * various types of connections (JIRA, LLM, File) with CRUD functionality
 */
@Component({
  selector: "app-connection",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormlyModule,
    FormlyBootstrapModule,
    MatIconModule,
    NoDataComponent,
    LoadingComponent,
    MaterialModule,
    MatProgressSpinnerModule,
    ConnectionCardComponent,
  ],
  templateUrl: "./connection.component.html",
  styleUrl: "./connection.component.scss",
})
export class ConnectionComponent extends BaseLayoutComponent implements OnInit {
  @Input() override dataStore!: DataStore;

  /**
   * Tabs data for JIRA, LLM, and File connections.
   */
  tabs: any = [];
  private messageService = inject(MessageService);
  /**
   * Reactive form group for the connection form.
   */
  form1 = new FormGroup({});
  form2 = new FormGroup({});
  form3 = new FormGroup({});
  model: any = {};
  options: FormlyFormOptions = {};
  data: any = {};
  buttonText: string = "Save";
  selectedConnection: any;

  /**
   * Root styles from the document.
   */
  rootStyles = getComputedStyle(document.documentElement);
  connectionData: any;
  tabIndex: number = 0;
  formData: any;
  selectedFile: any;
  pagedConnections: any[] = [];
  currentPage = 0;
  itemsPerPage = 12;
  totalPages = 0;

  isLoading: any = true;
  isAppContextLoading: boolean = false;
  fields: FormlyFieldConfig[] = connectionLLMTemplate;
  buttonTitle: string = "Add New LLM Connection";
  private modalService = inject(NgbModal);
  isTesting: boolean = false;
  clickedConnection: any;
  providerSectionsList: any[] = [];
  assets: any = [];
  /**
   * Observable for connections data.
   */
  connection$ = this.store.select(selectConnections);

  get currentTabType(): string {
    return this.tabs[this.tabIndex]?.type || "";
  }
  loadingConnection$: Observable<boolean> = this.store.select(
    selectConnectionLoading
  );
  errorConnection$: Observable<string | null> = this.store.select(
    selectConnectionError
  );
  isAgentic: boolean = false;
  isAlmConfigured = false;
  projectOrigin = "";

  get isManualProject(): boolean {
    return String(this.projectOrigin).toUpperCase() === "MANUAL";
  }

  private readonly storageFileAccept = [
    ".pdf",
    ".sql",
    ".xls",
    ".xlsx",
    ".doc",
    ".docx",
    ".csv",
    ".apk",
    ".ipa",
  ];

  get isOrgAlmLocked(): boolean {
    return this.currentTabType === "ALM" && !this.isManualProject;
  }

  get canAddConnection(): boolean {
    return !this.isOrgAlmLocked;
  }

  /**
   * Initializes the component.
   */
  ngOnInit(): void {
    // Initialize form model
    this.model = {
      connectionName: "",
      baseUrl: "",
      username: "",
      project: "",
      apiKey: "",
    };

    // Subscribe to connection data and update form
    this.connection$.subscribe((conn: any) => {
      // if (!this.selectedConnection) {
      // Prevent overwriting model after selection
      this.connectionData = conn || {};
      this.getDataForTabs(this.connectionData);
      this.updatePagedConnections();
      // }
    });

    this.data = this.getFormFields();
    this.getDataForTabs(this.connectionData);

    // Initialize the loading state
    this.isLoading = this.store.select(selectConnectionLoading);
    this.loadingConnection$.subscribe((loading) => {
      this.isLoading = loading;
    });

    let projectId = this.dataStore.data.id;
    this.store.dispatch(
      ApplicationDetailsActions.loadApplicationDetails({
        projectId: projectId,
      })
    );

    // Subscribe to the application details observable
    this.store.select(selectApplicationDetails).subscribe((details) => {
      this.assets = details || [];
      this.assets = this.assets.map((item: any) => ({ ...item }));
    });

    this.agenticAiService.aiApproach$.subscribe(approach => {
      this.isAgentic = approach === "AGENTIC_AI";
    });

    this.loadOrganizationAlmConfig();
  }

  private loadOrganizationAlmConfig(): void {
    this.restService.getProjectById(this.dataStore?.data?.id).subscribe({
      next: (response: any) => {
        this.projectOrigin = String(response?.data?.origin ?? response?.origin ?? "").toUpperCase();
      },
      error: () => {
        this.projectOrigin = "";
      },
    });
    const stored =
      sessionStorage.getItem("isAlmConfigured") ||
      localStorage.getItem("isAlmConfigured");
    this.isAlmConfigured = stored === "true";
    this.restService.getOrganizationConfig().subscribe({
      next: (response: any) => {
        const settings = response?.data ?? response;
        const value = settings?.isAlmConfigured ?? response?.isAlmConfigured;
        this.isAlmConfigured = value === true || value === "true";
      },
      error: () => {
        this.isAlmConfigured = false;
      },
    });
  }

  /**
   * Populates tab data based on the connections.
   * @param connections - Connections data object.
   */
  getDataForTabs(connections: any) {
    this.tabs = [
      {
        label: "LLM Connections",
        shortName: "LLM",
        fields: this.data?.llm,
        connections: connections?.LLM,
        type: "LLM",
      },
      {
        label: "ALM Connections",
        shortName: "ALM",
        fields: this.data?.jira,
        connections: connections?.ALM,
        type: "ALM",
      },
      {
        label: "Storage Connections",
        shortName: "Storage",
        fields: this.data?.file,
        connections: connections?.FILE,
        type: "FILE",
      },
      // DB Connections tab 
      {
        label: "DB Connections",
        shortName: "DB",
        fields: this.data?.jira,
        connections: connections?.DATABASE,
        type: "DATABASE",
      },
      {
        label: "Parser Connections",
        shortName: "Parser",
        fields: this.data?.file,
        connections: connections?.LLM_PARSER,
        type: "LLM_PARSER",
      },
    ];
  }

  /**
   * Retrieves connection data based on the model and type.
   * @param model - Data model.
   * @param isJira - Whether the connection is JIRA.
   * @returns Connection data object.
   */

  /**
   * Retrieves form fields for the connections.
   * @returns Object containing form fields.
   */
  getFormFields() {
    return {
      jira: connectionJiraTemplate,
      llm: connectionLLMTemplate,
      file: connectionFileTemplate(this.selectedFile, this.rootStyles),
    };
  }

  /**
   * Handles selection of a connection.
   * @param conn - Selected connection object.
   */
  onConnectionEditClick($event: any, conn: any) {
    if (this.isOrgAlmLocked) {
      return;
    }
    this.selectedConnection = conn;
    this.buttonText = this.selectedConnection ? "Update" : "Save";
    this.model = this.selectedConnection ? this.model : {};

    this.addConnection(true);
  }

  /**
   * Handles tab change event.
   * @param event - Tab change event object.
   */
  onTabChange(event: any) {
    this.tabIndex = event.index;
    this.updatePagedConnections();
    const shortName = this.tabs[this.tabIndex]?.shortName;
    if (shortName) {
      this.buttonTitle = `Add New ${shortName} Connection`;
    }
  }

  /**
   * Updates paginated connections for the current page.
   */
  updatePagedConnections() {
    // const start = this.currentPage * this.itemsPerPage;
    // const end = start + this.itemsPerPage;
    this.pagedConnections = this.tabs[this.tabIndex]?.connections
      ? this.tabs[this.tabIndex]?.connections
      : [];
    this.totalPages = Math.ceil(
      this.tabs[this.tabIndex]?.connections?.length / this.itemsPerPage
    );
    const seen = new Set<string>();
    this.providerSectionsList = this.pagedConnections
      .filter((conn) => {
        const source = conn.meta?.source;
        if (!source || seen.has(source)) return false;
        seen.add(source);
        return true;
      })
      .map((conn) => {
        const source = conn.meta.source;
        const displayName = conn.meta.displayName || source;
        return {
          key: source,
          label: displayName,
          expanded: true,
        };
      });
  }

  /**
   * Handles pagination navigation.
   * @param direction - Direction to navigate ("prev" or "next").
   */
  // changePage(direction: "prev" | "next") {
  //   if (direction === "prev" && this.currentPage > 0) {
  //     this.currentPage--;
  //   } else if (direction === "next" && this.currentPage < this.totalPages - 1) {
  //     this.currentPage++;
  //   }
  //   this.updatePagedConnections();
  // }

  deleteConnecetionConfirmation(event: Event, conn: any) {
    if (this.isOrgAlmLocked) {
      return;
    }

    // For FILE (Storage) connections validate usage before showing the
    // delete-confirmation modal — the file may be referenced in a script step.
    if (this.currentTabType === 'FILE') {
      this.restService
        .validateDeleteStorageConnection(this.dataStore?.data?.id, conn.id)
        .subscribe({
          next: (res: any) => {
            const inUseList: any[] = res?.data ?? [];
            if (inUseList.length > 0) {
              // Build a readable list of script names if the backend returns them
              const scriptNames = inUseList
                .map((entry: any) => entry.name ?? entry.scriptName ?? entry.id)
                .filter(Boolean)
                .join(', ');
              const detail = scriptNames ? ` (${scriptNames})` : '';
              this.toaster.pop({
                type: 'error',
                text: `"${conn.connectionName}" is used in a script${detail} and cannot be deleted. Remove it from all script steps first.`,
              });
            } else {
              this.openDeleteConfirmation(event, conn);
            }
          },
          error: () => {
            this.toaster.pop({
              type: 'error',
              text: 'Could not verify connection usage. Please try again.',
            });
          },
        });
      return;
    }

    this.openDeleteConfirmation(event, conn);
  }

  private openDeleteConfirmation(event: Event, conn: any) {
    const modalRef = this.modalService.open(DeleteConfirmationComponent, {
      windowClass: 'delete-confirmation-window',
      centered: true,
      size: 'sm',
      backdrop: 'static',
    });
    modalRef.result.then((result: any) => {
      if (result) {
        this.deleteConnection(event, conn);
      }
    });
  }

  deleteConnection(event: Event, conn: any) {
    event.stopPropagation();
    this.store.dispatch(
      ConnectionActions.deleteConnection({
        projectId: this.dataStore?.data?.id,
        connId: conn.id,
      })
    );
  }

  addConnection(isEdit: boolean) {
    if (this.isOrgAlmLocked) {
      return;
    }
    let fields;
    let displayName;
    let providers;
    let models: any;
    switch (this.currentTabType) {
      case "ALM":
        displayName = isEdit ? "Update ALM Connection" : "Add ALM Connection";
        this.openGenericModal("ALM", isEdit, displayName);
        break;
      case "LLM":
        displayName = isEdit ? "Update LLM Connection" : "Add LLM Connection";
        this.openGenericModal("LLM", isEdit, displayName);
        break;
      case "DATABASE":
        displayName = isEdit ? "Update DB Connection" : "Add DB Connection";
        this.openGenericModal("DATABASE", isEdit, displayName);
        break;
      case "LLM_PARSER":
        displayName = isEdit
          ? "Update Prser Connection"
          : "Add Parser Connection";
        this.createLlamaConnectionModal(
          isEdit,
          displayName,
          isEdit ? llamaConnFieldsEdit : llamaConnFields
        );
        break;
      case "FILE":
        displayName = isEdit
          ? "Update Storage Connection"
          : "Add New Storage Connection";
        fields = connectionFileTemplate(this.selectedFile, this.rootStyles);
        this.openFileConnModal(isEdit, displayName, fields);
        break;
      default:
        break;
    }
  }
  createLlamaConnectionModal(
    isEdit: boolean,
    displayName: string,
    fields: any
  ) {
    var data = {
      displayName: displayName,
      saveBtnText: isEdit ? "Update" : "Add",
    };

    const modalRef = this.modalService.open(AgentCreateModalComponent, {
      centered: true,
      windowClass: "add-item-modal",
      backdrop: 'static',
    });
    modalRef.componentInstance.configs = {
      title: data.displayName,
      saveBtnText: data.saveBtnText,
      isEdit: isEdit,
      model: isEdit
        ? {
            connectionName: this.selectedConnection?.connectionName,
            apiKey: this.selectedConnection?.meta?.credentials?.api_key,
          }
        : {},
    };
    modalRef.componentInstance.fields = fields;

    modalRef.result.then((result) => {
      if (result) {
        let data = {
          connectionName: result?.value?.connectionName
            ? result?.value?.connectionName
            : this.selectedConnection?.connectionName,
          connectionType: "LLM_PARSER",
          meta: {
            source: "LLAMA",
            credentials: {
              api_key: result.value.apiKey,
            },
          },
        };

        if (isEdit) {
          this.store.dispatch(
            ConnectionActions.updateConnection({
              projectId: this.dataStore?.data?.id,
              connId: this.selectedConnection.id,
              connection: data,
            })
          );
        } else {
          const existingConnection = this.pagedConnections.find(
            (connection) =>
              connection.connectionName.toLowerCase() ===
              data?.connectionName.toLowerCase()
          );

          if (existingConnection) {
            this.toaster.pop({
              type: "error",
              text: MessageEnum.CONNECTION_NAME_DUPLICATE,
            });
            return;
          } else {
            this.store.dispatch(
              ConnectionActions.createConnection({
                id: this.dataStore?.data?.id,
                connection: data,
              })
            );
          }
        }
      }
    });
  }

  openGenericModal(type: string, isEdit: boolean, displayName: string) {
    var data = {
      displayName: displayName,
      saveBtnText: isEdit ? "Update" : "Add",
    };

    const modalRef = this.modalService.open(ConnectionModalComponent, {
      centered: true,
      size: "lg",
      windowClass: "add-item-modal",
      backdrop: 'static',
    });
    modalRef.componentInstance.configs = {
      type: type,
      title: data.displayName,
      saveBtnText: data.saveBtnText,
      isDynamic: true,
      isEdit: isEdit,
      model: isEdit ? this.selectedConnection : {},
      
    };

    modalRef.result.then((result) => {
      let resultObj;
      if (result) {
        if (type === "LLM") {
          resultObj = {
            connectionName: result.value.connectionName,
            modelName: result.value.modelName,
            connectionType: type,
            meta: result.value.meta,
          };
        } else {
          resultObj = {
            connectionName: result.value.connectionName,
            connectionType: type,
            meta: result.value.meta,
          };
        }
        this.addUpdateConnection(isEdit, resultObj);
      }
    });
  }

  addUpdateConnection(isEdit: boolean, model: any, connectionFile?: any) {
    if (this.currentTabType === "ALM" || this.currentTabType === "LLM" || this.currentTabType === "DATABASE") {
      var name = this.selectedConnection?.connectionName
        ? this.selectedConnection?.connectionName
        : this.selectedConnection?.llmConnName;

      console.log("Prepared Data for submission:", model);

      if (!isEdit) {
        // Check for duplicate connection names
        const existingConnection = this.pagedConnections.find(
          (connection) =>
            connection.connectionName.toLowerCase() ===
            model.connectionName.toLowerCase()
        );

        if (existingConnection) {
          this.toaster.pop({
            type: "error",
            text: MessageEnum.CONNECTION_NAME_DUPLICATE,
          });
          this.model = {}; // Reset the form
          return;
        }
        this.store.dispatch(
          ConnectionActions.createConnection({
            id: this.dataStore?.data?.id,
            connection: model,
          })
        );
      } else {
        this.store.dispatch(
          ConnectionActions.updateConnection({
            projectId: this.dataStore?.data?.id,
            connId: this.selectedConnection.id,
            connection: model,
          })
        );
      }
    } else {
      // Handle File Connection Save
      console.log("Handling File Connection Save...");

      this.formData = new FormData();
      this.formData.append("type", model.type);
      if (model.connectionName) {
        this.formData.append("connectionName", model.connectionName);
      }
      if (model.targetAsset) {
        this.formData.append("targetApplications", model.targetAsset);
      }
      if (model.tags) {
        this.formData.append("tags", model.tags);
      }

      if (model.type === "local") {
        this.formData.append(
          "file",
          connectionFile ? connectionFile : model.fileUpload
        );

        if (!isEdit) {
          const connNameForCheck = model.connectionName || connectionFile?.name || "";
          const existingConnection = this.pagedConnections.find(
            (connection) =>
              connection.connectionName.toLowerCase() ===
              connNameForCheck.toLowerCase()
          );

          if (existingConnection) {
            this.toaster.pop({
              type: "error",
              text: MessageEnum.CONNECTION_NAME_DUPLICATE,
            });
            this.model = {};
            return;
          }
        }

        this.store.dispatch(
          ConnectionActions.createFileConnection({
            id: this.dataStore?.data?.id,
            connection: this.formData,
          })
        );
      } else {
        const data = {
          connectionName: model.connectionName,
          targetApplications: model.targetAsset,
          tags: model.tags,
          type: model.type,
          accessKeyID: model.accessKeyID,
          accessKeySecret: model.accessKeySecret,
          s3BucketName: model.s3BucketName,
          region: model.region,
        };
        this.store.dispatch(
          ConnectionActions.createConnection({
            id: this.dataStore?.data?.id,
            connection: data,
          })
        );
      }
      console.log("File save process completed.");
    }
  }

  openFileConnModal(isEdit: boolean, displayName: string, fields: any) {
    var data = {
      displayName: displayName,
      saveBtnText: isEdit ? "Update" : "Add",
    };

    let model = {
      connectionName: this.selectedConnection?.connectionName,
      type: this.selectedConnection?.meta?.type,
      targetAsset: this.selectedConnection?.targetApplications ?? null,
      tags: this.selectedConnection?.tags,
      accessKeyID: this.selectedConnection?.meta?.accessKeyID,
      accessKeySecret: this.selectedConnection?.meta?.accessKeySecret,
      s3BucketName: this.selectedConnection?.meta?.s3BucketName,
      region: this.selectedConnection?.meta?.region,
      fileUpload: null,
    };

    const modalRef = this.modalService.open(FormlyModalComponent, {
      centered: true,
      windowClass: "add-item-modal",
      backdrop: 'static',
      scrollable: true,
      size: 'lg',
    });
    modalRef.componentInstance.configs = {
      title: data.displayName,
      saveBtnText: data.saveBtnText,
      isEdit: isEdit,
      model: isEdit ? model : {},
      predefinedTags: [],
      allowedFileExtensions: this.storageFileAccept,
      fileAcceptByTarget: {
        testDataGeneration: this.storageFileAccept,
        testCaseGenerator: this.storageFileAccept,
      },
    };
    modalRef.componentInstance.fields = fields;

    modalRef.result.then((result) => {
      
      this.addUpdateConnection(
        isEdit,
        result.value,
        this.currentTabType === "FILE" ? result?.value?.fileUpload : null
      );
    });
  }

  testConnection(event: MouseEvent, conn: any) {
    this.isTesting = true;
    this.clickedConnection = conn;
    event.stopPropagation();

    if (this.currentTabType === "ALM") {
      this.restService
        .testConnection(this.dataStore?.data?.id, conn?.id)
        .subscribe(
          (response: any) => {
            this.isTesting = false;
            if (response?.status == 200) {
              this.toaster.pop({
                type: "success",
                text: MessageEnum.CONNECTION_SUCCESS,
              });
            } else {
              const message = response?.message
                ? response?.message
                : MessageEnum.CONNECTION_FAILURE;
              this.toaster.pop({ type: "error", text: message });
            }
          },
          (error: any) => {
            this.isTesting = false;
            const message = error?.message
              ? error?.message
              : MessageEnum.CONNECTION_FAILURE;
            this.toaster.pop({ type: "error", text: message });
          }
        );
    } else if (this.currentTabType === "DATABASE") {
      this.restService
        .testDbConnection(this.dataStore?.data?.id, conn?.id)
        .subscribe(
          (response: any) => {
            this.isTesting = false;
            if (response?.status == 200) {
              this.toaster.pop({
                type: "success",
                text: MessageEnum.DB_CONNECTION_SUCCESS,
              });
            } else {
              const message = response?.message
                ? response?.message
                : MessageEnum.DB_CONNECTION_FAILURE;
              this.toaster.pop({ type: "error", text: message });
            }
          },
          (error: any) => {
            this.isTesting = false;
            const message = error?.message
              ? error?.message
              : MessageEnum.DB_CONNECTION_FAILURE;
            this.toaster.pop({ type: "error", text: message });
          }
        );
    } else if(this.currentTabType === "LLM_PARSER"){
    //for parser conn
      this.restService
        .testParserConnection(this.dataStore?.data?.id, conn?.id)
        .subscribe(
          (response: any) => {
            this.isTesting = false;
            if (response?.status == 200) {
              this.toaster.pop({
                type: "success",
                text: MessageEnum.PARSER_CONNECTION_SUCCESS,
              });
            } else {
              const message = response?.message
                ? response?.message
                : MessageEnum.PARSER_CONNECTION_FAILURE;
              this.toaster.pop({ type: "error", text: message });
            }
          },
          (error: any) => {
            this.isTesting = false;
            const message = error?.message
              ? error?.message
              : MessageEnum.CONNECTION_FAILURE;
            this.toaster.pop({ type: "error", text: message });
          }
        );
    }else {
      this.restService
        .testLLMConnection(this.dataStore?.data?.id, conn?.id)
        .subscribe(
          (response: any) => {
            this.isTesting = false;
            if (response?.status == 200) {
              this.toaster.pop({
                type: "success",
                text: MessageEnum.LLM_CONNECTION_SUCCESS,
              });
            } else {
              const message = response?.message
                ? response?.message
                : MessageEnum.LLM_CONNECTION_FAILURE;
              this.toaster.pop({ type: "error", text: message });
            }
          },
          (error: any) => {
            this.isTesting = false;
            const message = error?.message
              ? error?.message
              : MessageEnum.CONNECTION_FAILURE;
            this.toaster.pop({ type: "error", text: message });
          }
        );
    }
  }

  // getConnectionsForProvider(providerKey: string, connectionsList: any[]): any[] {
  //   return connectionsList.filter(item => item?.meta?.source === providerKey);
  // }

  onConnectionExpand(conn: any) {
    this.selectedConnection = conn;

    let connection;
    if (this.currentTabType === "ALM" || this.currentTabType === "LLM" || this.currentTabType === "DATABASE") {
      connection = {
        ...conn,
        provider: conn?.meta?.source,
      };
    } else if (this.currentTabType === "FILE") {
      connection = {
        ...conn,
        source: conn?.meta?.type,
        fileName: conn?.meta?.fileName,
      };
    } else {
      connection = {
        connectionName: conn?.connectionName,
        connectionType: "LLM_PARSER",
        source: "LLaMA",
        apiKey: conn?.meta?.credentials?.api_key,
      };
    }

    const modalRef = this.modalService.open(DataDisplayModalComponent, {
      centered: true,
      size: "lg",
      windowClass: "add-item-modal",
      backdrop: 'static',
    });
    modalRef.componentInstance.mainFields = this.getExpandViewFields();
    modalRef.componentInstance.data = {
      model: connection?.meta?.model,
      ...connection,
    };
    modalRef.componentInstance.configs = {
      title: "Connection Details",
      subTitle:
        this.currentTabType === "FILE" || this.currentTabType === "LLM_PARSER" ? "" : "Dynamic Configuration",
      type: this.currentTabType === "ALM" ? "ALM" : this.currentTabType === "DATABASE" ? "DATABASE" : "LLM",
      isCustom: this.currentTabType === "FILE" || this.currentTabType === "LLM_PARSER" ? true : false,
    };

    modalRef.result.then((result) => {});
  }

  getExpandViewFields() {
    switch (this.currentTabType) {
      case "ALM":
      case "DATABASE":
        return [
          {
            key: "connectionName",
            name: "Connection Name",
            type: "text-input",
          },
          {
            key: "provider",
            name: "Provider",
            type: "text-input",
          },
        ];

      case "LLM":
        return [
          {
            key: "connectionName",
            name: "Connection Name",
            type: "text-input",
          },
          {
            key: "provider",
            name: "Provider",
            type: "text-input",
          },
          {
            key: "model",
            name: "Model",
            type: "text-input",
          },
        ];

      case "LLM_PARSER":
        return [
          {
            key: "connectionName",
            name: "Connection Name",
            type: "text-input",
          },
          {
            key: "connectionType",
            name: "connection Type",
            type: "text-input",
          },
          {
            key: "source",
            name: "Source",
            type: "text-input",
          },
          {
            key: "apiKey",
            name: "API Key",
            type: "password-input",
          },
        ];

      case "FILE":
        return [
          {
            key: "connectionName",
            name: "Connection Name",
            type: "text-input",
          },
          {
            key: "source",
            name: "Source",
            type: "text-input",
          },
          {
            key: "fileName",
            name: "File Name",
            type: "text-input",
          },
        ];

      default:
        return [];
    }
  }

  setupConnection(type: string, label: string) {
    if(type === "FILE"){
      this.setupAppContext(type, label);
    }else{
      const modalRef = this.modalService.open(ConfigureAssetLlmModalComponent, {
        centered: true,
        size: "lg",
        backdrop: 'static',
      });

      modalRef.componentInstance.connections = this.connectionData[type];
      modalRef.componentInstance.projectId = this.dataStore?.data?.id;
      modalRef.componentInstance.label = label;
      modalRef.componentInstance.type = type;
      modalRef.result.then((result) => {
        this.SavePayLoadData(result, type);
      });
    }
  }

  setupAppContext(type: string, label: string){
    const modalRef = this.modalService.open(SetAppContextModalComponent, {
      centered: true,
      size: "lg",
      backdrop: 'static',
    });

    modalRef.componentInstance.fileConnections = this.connectionData[type];
    modalRef.componentInstance.projectId = this.dataStore?.data?.id;
    modalRef.componentInstance.assets = this.assets;
  }

  SavePayLoadData(result: any, type: string) {
    if (type === "LLM") {
      let payload: any = {
        globalLLm: null,
        isCommonLLM: true,
        applicationLLMs: {},
      };
      if (result.isGlobal) {
        payload.globalLLm = result.globalConnection;
      } else {
        payload.isCommonLLM = false;
        payload.applicationLLMs = result.assets;
      }
      this.restService
        .configureLLM(this.dataStore?.data?.id, payload)
        .subscribe(
          () => {
            this.toaster.pop(
              {
                type: "success",
                text: MessageEnum.LLM_CONNECTION_CONFIGURED_SUCCESS
              },
              { backgroundColor: "transparent" }
            );
          },
          (error: any) => {
            this.toaster.pop(
              { type: "error", text: error?.message },
              { backgroundColor: "transparent" }
            );
          }
        );
    } else if (type === "LLM_PARSER") {
      let payload: any = {
        globalLLmParser: null,
        isCommonLLMParser: true,
        applicationLLMParserConnections: {},
      };
      if (result.isGlobal) {
        payload.globalLLmParser = result.globalConnection;
      } else {
        payload.isCommonLLMParser = false;
        payload.applicationLLMParserConnections = result.assets;
      }
      this.restService
        .configureLLaMa(this.dataStore?.data?.id, payload)
        .subscribe(
          () => {
            this.toaster.pop(
              {
                type: "success",
                text: MessageEnum.PARSER_CONNECTION_CONFIGURED_SUCCESS
              },
              { backgroundColor: "transparent" }
            );
          },
          (error: any) => {
            this.toaster.pop(
              { type: "error", text: error?.message },
              { backgroundColor: "transparent" }
            );
          }
        );
    }
  }
}
